"use client";

import { useState, useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Search, SearchX } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Card from "@/components/ui/Card";
import type { Company } from "@/types";

type CompaniesGridEnhancedProps = {
  companies: Company[];
  viewMode?: "grid" | "list";
  initialSearch?: string;
};

export default function CompaniesGridEnhanced({
  companies,
  viewMode = "grid",
  initialSearch = "",
}: CompaniesGridEnhancedProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    initialSearch || searchParams.get("search") || ""
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams.toString());
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    } else {
      params.delete("search");
    }
    params.delete("page"); // Reset page when searching
    router.push(`/empresas?${params.toString()}`);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    const params = new URLSearchParams(searchParams.toString());
    params.delete("search");
    params.delete("page");
    router.push(`/empresas?${params.toString()}`);
  };

  const sortedCompanies = useMemo(() => {
    // Separa patrocinados e não patrocinados mantendo a ordem original da API
    const sponsored = companies.filter((c) => c.sponsored);
    const notSponsored = companies.filter((c) => !c.sponsored);

    // Patrocinados sempre nos primeiros índices, depois o resto na ordem da API
    return [...sponsored, ...notSponsored];
  }, [companies]);

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar empresas por nome, descrição ou categoria..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mygreen/20"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </form>

      {/* Results count */}
      <p className="text-sm text-gray-600">
        {sortedCompanies.length}{" "}
        {sortedCompanies.length === 1
          ? "empresa encontrada"
          : "empresas encontradas"}
      </p>

      {/* Grid/List */}
      {sortedCompanies.length === 0 ? (
        <div className="text-center py-16">
          <SearchX className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhuma empresa encontrada
          </h3>
          <p className="text-gray-600">
            Tente ajustar seus filtros ou termo de busca
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sortedCompanies.map((company) => (
            <Card
              key={company.id}
              variant="vertical"
              name={company.name}
              description={company.description || "Sem descrição disponível"}
              image={company.logo}
              href={`/empresa/${company.slug}`}
              sponsored={company.sponsored}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {sortedCompanies.map((company) => (
            <Card
              key={company.id}
              variant="horizontal"
              name={company.name}
              description={company.description || "Sem descrição disponível"}
              image={company.logo}
              href={`/empresa/${company.slug}`}
              sponsored={company.sponsored}
            />
          ))}
        </div>
      )}
    </div>
  );
}
